import { useEffect, useRef, useState } from 'react';
import {
  Upload,
  Download,
  Trash2,
  Loader2,
  File as FileIcon,
  FileText,
  FileSpreadsheet,
  Image as ImageIcon,
  Paperclip,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ConfirmModal } from '@/components/ConfirmModal';
import { toast } from '@/components/ui/toast';
import api from '@/lib/api';
import { formatDateTime } from '@/lib/dateFormat';

interface LeadDocument {
  id: string;
  fileName: string;
  mimeType: string;
  size: number;
  uploadedByName?: string | null;
  createdAt: string;
}

const MAX_BYTES = 20 * 1024 * 1024; // keep in sync with backend LEAD_DOC_MAX_BYTES

function formatBytes(bytes: number): string {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const val = bytes / Math.pow(1024, i);
  return `${val >= 10 || i === 0 ? Math.round(val) : val.toFixed(1)} ${units[i]}`;
}

function DocIcon({ mime, name }: { mime: string; name: string }) {
  const m = (mime || '').toLowerCase();
  const ext = (name.split('.').pop() || '').toLowerCase();
  const cls = 'h-5 w-5';
  if (m.startsWith('image/')) return <ImageIcon className={`${cls} text-violet-500`} />;
  if (m === 'application/pdf' || ext === 'pdf') return <FileText className={`${cls} text-red-500`} />;
  if (m.includes('sheet') || m.includes('excel') || ['xls', 'xlsx', 'csv'].includes(ext))
    return <FileSpreadsheet className={`${cls} text-emerald-600`} />;
  if (m.includes('word') || ['doc', 'docx'].includes(ext))
    return <FileText className={`${cls} text-blue-500`} />;
  return <FileIcon className={`${cls} text-slate-400`} />;
}

export function LeadDocumentsTab({ leadId }: { leadId: string }) {
  const [docs, setDocs] = useState<LeadDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<LeadDocument | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    try {
      const res = await api.get(`/leads/${leadId}/documents`);
      setDocs(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast('error', 'Could not load documents.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const uploadFiles = async (files: FileList | File[]) => {
    const list = Array.from(files);
    if (!list.length) return;
    setUploading(true);
    let ok = 0;
    for (const file of list) {
      if (file.size > MAX_BYTES) {
        toast('error', `${file.name} is larger than 20 MB — skipped.`);
        continue;
      }
      try {
        const form = new FormData();
        form.append('file', file);
        await api.post(`/leads/${leadId}/documents`, form);
        ok += 1;
      } catch (err: any) {
        toast('error', err?.response?.data?.message || `Failed to upload ${file.name}.`);
      }
    }
    setUploading(false);
    if (ok > 0) {
      toast('success', ok === 1 ? 'Document uploaded.' : `${ok} documents uploaded.`);
      load();
    }
  };

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) uploadFiles(e.target.files);
    e.target.value = '';
  };

  const download = async (doc: LeadDocument) => {
    setDownloadingId(doc.id);
    try {
      // Fetch as a blob so the axios bearer token is attached (the file
      // streams from an authenticated endpoint), then trigger a save.
      const res = await api.get(`/leads/${leadId}/documents/${doc.id}/download`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast('error', 'Could not download the file.');
    } finally {
      setDownloadingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/leads/${leadId}/documents/${toDelete.id}`);
      setDocs((prev) => prev.filter((d) => d.id !== toDelete.id));
      toast('success', 'Document deleted.');
      setToDelete(null);
    } catch {
      toast('error', 'Could not delete the document.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Paperclip className="h-4 w-4" />
            Documents
            {docs.length > 0 && (
              <span className="rounded-full bg-slate-100 px-2 text-xs font-semibold text-slate-600">
                {docs.length}
              </span>
            )}
          </CardTitle>
          <Button size="sm" onClick={() => inputRef.current?.click()} disabled={uploading}>
            {uploading ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Upload className="mr-1.5 h-4 w-4" />
            )}
            Upload File
          </Button>
          <input ref={inputRef} type="file" multiple className="hidden" onChange={onPick} />
        </div>
      </CardHeader>

      <CardContent>
        {/* Drop zone */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files);
          }}
          onClick={() => inputRef.current?.click()}
          className={`mb-4 flex cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors ${
            dragOver
              ? 'border-[#6366F1] bg-indigo-50/60'
              : 'border-slate-200 bg-slate-50/60 hover:border-slate-300'
          }`}
        >
          <Upload className="h-6 w-6 text-slate-400" />
          <p className="text-sm font-medium text-slate-600">
            Drop files here or click to upload
          </p>
          <p className="text-xs text-slate-400">
            Any file — PDF, image, Excel, Word, etc. Up to 20 MB each.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-6 text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : docs.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">
            No documents yet. Upload bills, receipts or any file for this lead.
          </p>
        ) : (
          <div className="divide-y divide-slate-100 rounded-lg border border-slate-100">
            {docs.map((doc) => (
              <div key={doc.id} className="flex items-center gap-3 px-3 py-2.5">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-50">
                  <DocIcon mime={doc.mimeType} name={doc.fileName} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-800" title={doc.fileName}>
                    {doc.fileName}
                  </p>
                  <p className="truncate text-xs text-slate-400">
                    {formatBytes(doc.size)}
                    {' · '}
                    {formatDateTime(doc.createdAt)}
                    {doc.uploadedByName ? ` · ${doc.uploadedByName}` : ''}
                  </p>
                </div>
                <button
                  onClick={() => download(doc)}
                  disabled={downloadingId === doc.id}
                  title="Download"
                  className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-[#6366F1] disabled:opacity-50"
                >
                  {downloadingId === doc.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                </button>
                <button
                  onClick={() => setToDelete(doc)}
                  title="Delete"
                  className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <ConfirmModal
        isOpen={!!toDelete}
        title="Delete document?"
        message={`"${toDelete?.fileName ?? ''}" will be permanently removed. This cannot be undone.`}
        confirmText="Delete"
        onConfirm={confirmDelete}
        onCancel={() => (deleting ? null : setToDelete(null))}
        isLoading={deleting}
      />
    </Card>
  );
}
